import {
  IonDatetime,
  IonIcon,
  IonInput,
  IonItem,
  IonLabel,
  IonList,
  IonSearchbar,
  IonSelect,
  IonSelectOption,
} from '@ionic/react';
import { ForkKnifeIcon } from '@phosphor-icons/react';
import React from 'react';

import { checkmark, pencilSharp, trashSharp } from 'ionicons/icons';

import { useAlimentacionHelper } from '../../../hooks/Alimentacion-Pesaje/useAlimentacionHelper';
import Button from '../../UI/Button';
import FormHeader from '../../UI/FormHeader';
import FormItem from '../../UI/FormItem';
import Modal from '../../UI/Modal';
import Spinner from '../../UI/Spinner';
import './Alimentacion.scss';
import { AlimentacionProps } from './types';

const Alimentacion: React.FC<AlimentacionProps> = (props) => {
  const {
    isLoading,
    isDeleting,
    modalOpen,
    search,
    editingAlimento,
    alimentosByMascota,
    presentacionesAlimento,
    selectedNombreAlimento,
    today,
    control,
    errors,
    handleSubmit,
    enterRef,
    handleEnter,
    handleSelectOption,
    handleSearchChange,
    handleOpenModal,
    handleCloseModal,
    handleOpenAlimentoModal,
    onSubmit,
    handleUpdate,
    confirmDelete,
    getModalItems,
    view,
    setView,
  } = useAlimentacionHelper(props);

  const isEditing = !!editingAlimento;

  return (
    <div className="alimentacion">
      {isDeleting && <Spinner fullscreen />}

      {view === null && (
        <>
          <FormHeader
            icon={<ForkKnifeIcon />}
            title="Alimentación"
            subtitle={`Registrá la alimentación de ${props.nombre}`}
          />

          <div className="ion-padding">
            <Button marginBottom={false} onClick={() => setView('agregar')}>
              Agregar alimento
            </Button>
            <Button noMarginTop onClick={() => setView('historial')}>
              Historial de alimentación
            </Button>
          </div>
        </>
      )}

      {view === 'agregar' && (
        <>
          <FormHeader
            icon={<ForkKnifeIcon />}
            title={isEditing ? 'Editar alimento' : 'Nuevo alimento'}
            subtitle={
              isEditing
                ? `Editá el alimento de ${props.nombre}`
                : `Agregá un nuevo alimento para ${props.nombre}`
            }
          />
          <form
            onSubmit={handleSubmit(onSubmit)}
            onKeyUp={handleEnter}
            className="ion-padding-horizontal"
          >
            <FormItem
              name="marca"
              control={control}
              errors={errors}
              rules={{ required: 'Por favor selecciona una marca' }}
              render={({ value }) => (
                <IonItem
                  onClick={isEditing ? undefined : () => handleOpenModal('marca')}
                  disabled={isEditing}
                >
                  <IonLabel position="floating">Marca</IonLabel>
                  <IonInput value={value} readonly disabled={isEditing} />
                </IonItem>
              )}
            />

            <FormItem
              name="nombre"
              control={control}
              errors={errors}
              rules={{ required: 'Por favor selecciona un alimento' }}
              render={({ value }) => (
                <IonItem
                  onClick={isEditing ? undefined : handleOpenAlimentoModal}
                  disabled={isEditing}
                >
                  <IonLabel position="floating">Alimento</IonLabel>
                  <IonInput value={value} readonly disabled={isEditing} />
                </IonItem>
              )}
            />

            <FormItem
              name="peso"
              control={control}
              errors={errors}
              rules={{ required: 'Por favor selecciona una presentación' }}
              render={({ value, onChange }) => (
                <IonItem>
                  <IonLabel position="floating">Presentación (Kg)</IonLabel>
                  <IonSelect
                    value={value}
                    placeholder="Selecciona presentación"
                    onIonChange={(e) => onChange(e.detail.value)}
                    disabled={
                      !isEditing &&
                      (!selectedNombreAlimento || presentacionesAlimento.length === 0)
                    }
                  >
                    {presentacionesAlimento.map((p) => (
                      <IonSelectOption key={p} value={p}>
                        {p} Kg
                      </IonSelectOption>
                    ))}
                  </IonSelect>
                </IonItem>
              )}
            />

            <FormItem
              name="fechaCompra"
              control={control}
              errors={errors}
              rules={{
                required: isEditing ? false : 'Por favor ingresa la fecha de compra',
              }}
              render={({ value, onChange }) => (
                <IonItem disabled={isEditing}>
                  <IonLabel position="floating">Fecha de compra</IonLabel>
                  <IonDatetime
                    value={value}
                    onIonChange={(e) => onChange(e.detail.value)}
                    displayFormat="DD-MM-YYYY"
                    max={today}
                    doneText="Confirmar"
                    cancelText="Cancelar"
                    disabled={isEditing}
                  />
                </IonItem>
              )}
            />

            <div className="ion-margin-top">
              <Button ref={enterRef} type="submit" disabled={isLoading === 'submit'}>
                {isLoading === 'submit' ? (
                  <Spinner color="light" />
                ) : isEditing ? (
                  'Guardar cambios'
                ) : (
                  'Agregar alimento'
                )}
              </Button>
            </div>
          </form>
        </>
      )}

      <Modal isOpen={modalOpen !== null} onClose={handleCloseModal}>
        <IonSearchbar
          value={search}
          onIonInput={(e) =>
            handleSearchChange((e.target as HTMLIonSearchbarElement).value || '')
          }
          placeholder={`Buscar ${modalOpen}`}
        />
        <IonList>
          {getModalItems().map(({ id, label, isSelected }) => (
            <IonItem
              key={id}
              button
              onClick={() => handleSelectOption(modalOpen!, String(id))}
            >
              {label}
              {isSelected && <IonIcon icon={checkmark} slot="end" color="primary" />}
            </IonItem>
          ))}
        </IonList>
      </Modal>

      {view === 'historial' && (
        <div className="historial-alimentos">
          {alimentosByMascota?.length ? (
            alimentosByMascota.map((item) => (
              <IonItem key={item.id}>
                <IonLabel>
                  <h2>
                    {item.alimento.nombre} ({item.alimento.marca})
                  </h2>
                  <p>{item.total} kg</p>
                </IonLabel>

                <IonIcon
                  icon={pencilSharp}
                  color="primary"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleUpdate(item);
                  }}
                  className="aux-icon-spacing"
                />

                <IonIcon
                  icon={trashSharp}
                  color="danger"
                  onClick={(e) => {
                    e.stopPropagation();
                    confirmDelete(item.id);
                  }}
                />
              </IonItem>
            ))
          ) : (
            <div className="alimentacion__empty-state">
              <div className="alimentacion__empty-icon">
                <ForkKnifeIcon weight="regular" />
              </div>
              <h3 className="alimentacion__empty-title">Sin alimentos registrados</h3>
              <p className="alimentacion__empty-description">
                Aún no hay alimentos registrados para {props.nombre}.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default React.memo(Alimentacion);
